import Button from '@rio-cloud/rio-uikit/Button';
import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import { parseISO } from 'date-fns';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import { mapContractPeriod } from '../../api/periods';
import type { ApiProduct } from '../../api/product/product.types';
import { isBookableProduct, isMultiLevelProduct, type Product, type ProductLevel } from '../../checkout/redux/types';
import { DescriptionOfServiceContainer } from '../../common/descriptionOfService/DescriptionOfServiceContainer';
import { getEstimatedPrice, getFullTermPrice } from '../../common/price/priceUtil';
import { ProductType } from '../../common/product/product';
import type { TermedLevel } from '../../common/termedLevel/types';
import { TermsAndConditionsContainer } from '../../common/termsAndConditions/TermsAndConditionsContainer';
import { formatDurationUnit } from '../../common/utils/formatDurationUnit';
import { resolveContractDocuments } from '../contracts/contractDocumentResolution';
import { mapResourceTypeToProductType } from '../contracts/mapResourceTypeToProductType';
import { BookingOverviewContractState, RenewalDropdown } from './BookingOverviewContractTable';
import { BookingOverviewRemainingDays } from './BookingOverviewRemainingDays';
import './BookingOverviewContractSidebar.css';

interface Props {
    contract?: TermedLevel;
    product?: Product;
    productDetails?: ApiProduct;
    assetType?: string;
    canConfigureRenewal: boolean;
    onConfigureRenewal: (contract: TermedLevel) => void;
    onClose: () => void;
}

const message = (id: string) => <FormattedMessage id={id} />;

type ProductLevelWithPrice = Pick<ProductLevel, 'price' | 'currency' | 'contractPeriod'>;

function getProductLevel(
    productDetails: ApiProduct | undefined,
    product: Product | undefined,
    level: string
): ProductLevelWithPrice | undefined {
    const ratePlan = productDetails?.rate_plans?.find(ratePlan => ratePlan.level === level);
    if (ratePlan) {
        return {
            price: ratePlan.price,
            currency: ratePlan.currency,
            contractPeriod: mapContractPeriod(ratePlan.contract_period),
        };
    }

    if (isMultiLevelProduct(product)) {
        return product.levels.find(productLevel => productLevel.level === level);
    }

    return isBookableProduct(product) && product.level === level ? product : undefined;
}

function getAssetIcon(assetType: string | undefined, productType: ProductType) {
    if (productType === ProductType.USER_BASED) {
        return 'icon-user';
    }

    switch (assetType?.toLowerCase()) {
        case 'bus':
            return 'icon-bus';
        case 'van':
            return 'icon-van';
        case 'trailer':
            return 'icon-trailer';
        default:
            return 'icon-truck';
    }
}

function Detail({
    label,
    value,
    compact = false,
}: {
    label: React.ReactNode;
    value?: React.ReactNode;
    compact?: boolean;
}) {
    return (
        <div className='BookingDetailCard display-flex flex-column bg-white shadow-subtle rounded padding-x-15 padding-y-10 flex-1-1-0'>
            <span className='text-color-dark text-size-12 margin-top--5'>{label}</span>
            <div className={`text-bold ${compact ? 'text-size-14' : 'text-size-16'}`}>{value ?? '–'}</div>
        </div>
    );
}

function SectionTitle({ iconName, children }: { iconName: string; children: React.ReactNode }) {
    return (
        <div className='text-bold text-color-darker display-flex align-items-center gap-5 margin-top-10 margin-bottom-5'>
            <span className={`rioglyph ${iconName} text-size-16`} aria-hidden />
            {children}
        </div>
    );
}

function Costs({ productLevel, contract }: { productLevel?: ProductLevelWithPrice; contract: TermedLevel }) {
    const intl = useIntl();
    if (!productLevel) {
        return <div className='bg-lightest rounded padding-10'>–</div>;
    }

    const { price, currency } = productLevel;
    const formatCost = (value: number) =>
        `${intl.formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
    const period =
        contract.periodLength && contract.periodUnit
            ? { length: contract.periodLength, unit: contract.periodUnit }
            : productLevel.contractPeriod;

    return (
        <div className='BookingCostPanel bg-lightest rounded padding-x-15 padding-y-10 display-flex flex-column'>
            <div className='display-flex justify-content-between gap-15'>
                <span>{message('intl-msg:marketplace.contractDetails.cost.daily')}</span>
                <strong>{formatCost(price)}</strong>
            </div>
            <div className='display-flex justify-content-between gap-15'>
                <span>{message('intl-msg:marketplace.contractDetails.cost.monthly')}</span>
                <strong>{formatCost(getEstimatedPrice(price))}</strong>
            </div>
            <div className='display-flex justify-content-between gap-15'>
                <span>{message('intl-msg:marketplace.contractDetails.cost.yearly')}</span>
                <strong>{formatCost(price * 365)}</strong>
            </div>
            {period && (
                <div className='display-flex justify-content-between gap-15'>
                    <span>{message('intl-msg:marketplace.contractDetails.cost.fullTerm')}</span>
                    <strong>{formatCost(getFullTermPrice(price, period))}</strong>
                </div>
            )}
        </div>
    );
}

export function BookingOverviewContractSidebar({
    contract,
    product,
    productDetails,
    assetType,
    canConfigureRenewal,
    onConfigureRenewal,
    onClose,
}: Props) {
    const intl = useIntl();
    const [isShowingTerms, setIsShowingTerms] = useState(false);
    const [isShowingDescription, setIsShowingDescription] = useState(false);
    const productLevel = contract ? getProductLevel(productDetails, product, contract.level) : undefined;
    const productType = product?.productType ?? mapResourceTypeToProductType(contract?.resourceType ?? 'asset');
    const documents = contract ? resolveContractDocuments(contract) : undefined;

    return createPortal(
        <div className={`BookingOverviewSidebar${contract ? '' : ' display-none'}`}>
            <Sidebar
                closed={!contract}
                onClose={onClose}
                position={Sidebar.RIGHT}
                width={450}
                title={message('intl-msg:marketplace.contractDetails.bookingDetails')}
                titleClassName='padding-left-5'
                bodyClassName='padding-x-15 padding-bottom-15'
            >
                {contract && (
                    <>
                        {documents?.canOpenTos && isShowingTerms && (
                            <TermsAndConditionsContainer
                                serviceSku={documents.productId}
                                level={contract.level}
                                documentVersion={documents.tosVersion}
                                show={isShowingTerms}
                                onClose={() => setIsShowingTerms(false)}
                            />
                        )}
                        {documents?.canOpenDos && isShowingDescription && (
                            <DescriptionOfServiceContainer
                                serviceSku={documents.productId}
                                level={contract.level}
                                documentVersion={documents.dosVersion}
                                show={isShowingDescription}
                                onClose={() => setIsShowingDescription(false)}
                            />
                        )}

                        <section className='BookingDetailGroup bg-lightest rounded padding-10'>
                            <div className='bg-white shadow-subtle rounded padding-x-15 padding-y-10 display-flex align-items-center justify-content-between gap-15'>
                                <div className='display-flex flex-column'>
                                    <span className='text-color-dark text-size-12 margin-top--5'>
                                        {message('intl-msg:marketplace.contractDetails.vehicle')}
                                    </span>
                                    <strong>{contract.resourceName || contract.resourceId}</strong>
                                </div>
                                <span
                                    className={`rioglyph ${getAssetIcon(assetType, productType).replace('icon-', 'rioglyph-')} text-size-h2`}
                                    aria-hidden
                                />
                            </div>
                            <div className='display-flex gap-10 margin-top-10'>
                                <Detail
                                    compact
                                    label={message('intl-msg:marketplace.contractDetails.product')}
                                    value={contract.productName}
                                />
                                <Detail
                                    label={message('intl-msg:marketplace.contractDetails.variant')}
                                    value={
                                        contract.variantShortName?.trim() ? (
                                            <span className='label label-primary label-filled label-condensed'>
                                                {contract.variantShortName}
                                            </span>
                                        ) : undefined
                                    }
                                />
                            </div>
                            {(contract.resourceLicensePlate || contract.resourceVin) && (
                                <div className='display-flex gap-10 margin-top-10'>
                                    {contract.resourceLicensePlate && (
                                        <Detail
                                            label={message('intl-msg:marketplace.contractDetails.licensePlate')}
                                            value={contract.resourceLicensePlate}
                                        />
                                    )}
                                    {contract.resourceVin && <Detail label='VIN' value={contract.resourceVin} />}
                                </div>
                            )}
                            {canConfigureRenewal && productType !== ProductType.USER_BASED && (
                                <a
                                    className='btn btn-primary btn-link btn-link-inline padding-0 margin-top-10 display-inline-flex align-items-center gap-5'
                                    href={`https://administration.assets.rio.cloud/#/assets/${encodeURIComponent(contract.resourceId)}`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    {message('intl-msg:marketplace.contractDetails.openFleetAdministration')}
                                    <svg
                                        width='14'
                                        height='14'
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        stroke='currentColor'
                                        strokeWidth='2'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        aria-hidden='true'
                                        focusable='false'
                                    >
                                        <path d='M14 3h7v7M21 3L10 14M10 3H3v18h18v-7' />
                                    </svg>
                                </a>
                            )}
                        </section>

                        <section>
                            <SectionTitle iconName='rioglyph-signature'>
                                {message('intl-msg:marketplace.contractDetails.contract')}
                            </SectionTitle>
                            <div className='BookingDetailGroup bg-lightest rounded padding-10'>
                                <div className='display-flex gap-10'>
                                    <Detail
                                        label={message('intl-msg:marketplace.bookings.overview.table.status')}
                                        value={
                                            <div
                                                className={`BookingOverviewStatus BookingOverviewStatus--${contract.contractState}`}
                                            >
                                                <BookingOverviewContractState contract={contract} />
                                            </div>
                                        }
                                    />
                                    <Detail
                                        label={message('intl-msg:marketplace.bookings.overview.table.renewsTo')}
                                        value={
                                            <RenewalDropdown
                                                contract={contract}
                                                variantName={contract.variantShortName?.trim() ?? ''}
                                                canConfigureRenewal={canConfigureRenewal}
                                                onConfigureRenewal={onConfigureRenewal}
                                            />
                                        }
                                    />
                                </div>
                                <div className='display-flex gap-10 margin-top-10'>
                                    <Detail
                                        label={message('intl-msg:marketplace.bookings.overview.table.period')}
                                        value={
                                            contract.periodLength && contract.periodUnit
                                                ? `${contract.periodLength} ${formatDurationUnit(
                                                      contract.periodUnit,
                                                      intl,
                                                      contract.periodLength
                                                  )}`
                                                : undefined
                                        }
                                    />
                                    <Detail
                                        label={message('intl-msg:marketplace.bookings.overview.table.daysLeft')}
                                        value={<BookingOverviewRemainingDays contract={contract} />}
                                    />
                                </div>
                                <div className='display-flex gap-10 margin-top-10'>
                                    <Detail
                                        label={message('intl-msg:marketplace.contractDetails.start')}
                                        value={
                                            contract.startsAt &&
                                            intl.formatDate(parseISO(contract.startsAt), {
                                                day: 'numeric',
                                                month: 'numeric',
                                                year: 'numeric',
                                            })
                                        }
                                    />
                                    <Detail
                                        label={message('intl-msg:marketplace.contractDetails.end')}
                                        value={
                                            contract.endsAt
                                                ? intl.formatDate(parseISO(contract.endsAt), {
                                                      day: 'numeric',
                                                      month: 'numeric',
                                                      year: 'numeric',
                                                  })
                                                : contract.startsAt
                                                  ? '∞'
                                                  : undefined
                                        }
                                    />
                                </div>
                            </div>
                        </section>

                        <section>
                            <SectionTitle iconName='rioglyph-currency-euro'>
                                {message('intl-msg:marketplace.contractDetails.estimatedCosts')}
                            </SectionTitle>
                            <Costs productLevel={productLevel} contract={contract} />
                        </section>

                        <section>
                            <SectionTitle iconName='rioglyph-pdf-file'>
                                {message('intl-msg:marketplace.contractDetails.documents')}
                            </SectionTitle>
                            <div className='BookingLegalDocuments bg-lightest rounded padding-x-15 padding-y-15 display-flex flex-column align-items-start gap-5'>
                                <Button
                                    className='btn btn-primary btn-link btn-link-inline'
                                    iconName='icon-link'
                                    disabled={!documents?.canOpenDos}
                                    onClick={() => setIsShowingDescription(true)}
                                >
                                    {message('intl-msg:marketplace.contractDetails.serviceDescription')}
                                </Button>
                                <Button
                                    className='btn btn-primary btn-link btn-link-inline'
                                    iconName='icon-link'
                                    disabled={!documents?.canOpenTos}
                                    onClick={() => setIsShowingTerms(true)}
                                >
                                    {message('intl-msg:marketplace.contractDetails.terms')}
                                </Button>
                            </div>
                        </section>
                    </>
                )}
            </Sidebar>
        </div>,
        document.body
    );
}
